import { makeChoices } from "../../distractors";
import { clean, numStr, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

const PERCENTS = [5, 10, 20, 25, 30, 40, 50, 60, 75, 80] as const;

function tier1(rng: Rng): QuestionDraft {
  const p = rng.pick(PERCENTS);
  const n = rng.int(1, 15) * 20;
  const result = (n * p) / 100;
  const choice4 = makeChoices(rng, result, [(n * p) / 10, n - result, p, (n * (100 - p)) / 100], (r) =>
    r.int(1, 200),
  );
  return q(
    both(`Oblicz ${p}% liczby ${n}.`, `Find ${p}% of ${n}.`),
    numeric(result),
    [`${p}% to ${p}/100.`, `${n} · ${p}/100 = ${result}.`],
    [`${p}% means ${p}/100.`, `${n} × ${p}/100 = ${result}.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const n = rng.pick([20, 25, 40, 50, 80, 100, 200, 250, 300, 400, 500]);
  const p = rng.pick(PERCENTS);
  const m = (n * p) / 100;
  if (!Number.isInteger(m)) return tier2(rng);
  const choice4 = makeChoices(rng, p, [100 - p, m, p * 2, p + 10, p - 10], (r) => r.int(1, 99));
  const ratioPl = numStr(clean(m / n), "pl");
  const ratioEn = numStr(clean(m / n), "en");
  return q(
    both(`Jakim procentem liczby ${n} jest liczba ${m}?`, `What percent of ${n} is ${m}?`),
    numeric(p),
    [`Dzielimy część przez całość: ${m} : ${n} = ${ratioPl}.`, `${ratioPl} · 100% = ${p}%.`],
    [`Divide the part by the whole: ${m} ÷ ${n} = ${ratioEn}.`, `${ratioEn} × 100% = ${p}%.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const price = rng.int(2, 20) * 10;
  const p = rng.pick([10, 20, 25, 30, 40, 50]);
  const change = (price * p) / 100;
  const decrease = rng.chance(0.6);
  const result = decrease ? price - change : price + change;
  const item = rng.pick([
    { pl: "Mikstura", en: "A potion" },
    { pl: "Zwój podpowiedzi", en: "A hint scroll" },
    { pl: "Żelazny miecz", en: "An iron sword" },
    { pl: "Bomba pieprzowa", en: "A pepper bomb" },
  ]);
  const choice4 = makeChoices(
    rng,
    result,
    [decrease ? price + change : price - change, price - p, change],
    (r) => r.int(1, 30) * 10,
  );
  return q(
    both(
      `${item.pl} kosztuje ${price} monet. Milo ${decrease ? "obniża" : "podnosi"} cenę o ${p}%. Ile kosztuje teraz?`,
      `${item.en} costs ${price} gold. Milo ${decrease ? "lowers" : "raises"} the price by ${p}%. What is the new price?`,
    ),
    numeric(result),
    [
      `${p}% z ${price} to ${price} · ${p}/100 = ${change}.`,
      `${price} ${decrease ? "−" : "+"} ${change} = ${result}.`,
    ],
    [
      `${p}% of ${price} is ${price} × ${p}/100 = ${change}.`,
      `${price} ${decrease ? "−" : "+"} ${change} = ${result}.`,
    ],
    { choice4 },
  );
}

export const percent: TopicDef = {
  id: "sp6.percent",
  level: "sp6",
  name: text("Procenty", "Percentages"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
